import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ImageIcon, Upload, AlertCircle } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from '@/components/ui/use-toast';

interface ImageUploaderProps {
  onChange: (file: File) => void;
  isUploading: boolean;
  imagePreview: string | null;
  altText?: string;
  uploadText?: string;
  uploadingText?: string;
  onError?: (error: string) => void;
}

export function ImageUploader({
  onChange,
  isUploading,
  imagePreview,
  altText = 'Previsualización de la imagen',
  uploadText = 'Subir imagen',
  uploadingText = 'Subiendo...',
  onError,
}: ImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const MAX_FILE_SIZE = 1024 * 1024 * 10; // 10 MB in bytes

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        const errorMessage =
          'El archivo es demasiado grande. El tamaño máximo permitido es 10 MB.';
        setError(errorMessage);

        // Use custom onError or default toast behavior
        if (onError) {
          onError(errorMessage);
        } else {
          // Default behavior: show toast
          toast({
            title: 'Error al subir imagen',
            description: errorMessage,
            variant: 'destructive',
          });
        }

        // Clear the input
        event.target.value = '';
        return;
      }

      // Clear error if file is valid
      setError(null);
      onChange(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      {/* Informative label */}
      <div className="text-sm text-gray-600">
        <p>Sube una imagen para el banner (máximo 10 MB)</p>
        <p className="text-xs text-gray-500">Formatos soportados: JPG y PNG</p>
      </div>

      {/* Image container */}
      <div className="relative h-64 w-full overflow-hidden rounded-lg bg-gray-100">
        {imagePreview ? (
          <img
            src={imagePreview}
            alt={altText}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ImageIcon className="h-16 w-16 text-gray-400" />
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        <Label
          htmlFor="bannerImage"
          className="absolute bottom-2 left-2 cursor-pointer"
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isUploading}
            onClick={handleClick}
          >
            <Upload className="mr-2 h-4 w-4" />
            {isUploading ? uploadingText : uploadText}
          </Button>
        </Label>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-600">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
